'use strict';

const VimeoPlayer = require('@vimeo/player');
const isMobile = require('./modules/util/isMobile');
const isMobileWidth = require('./modules/util/isMobileWidth');
const elementInViewport = require('./modules/util/elementInViewport');
const getQueryStringParams = require('./modules/util/getQueryStringParams');
const debounce = require('./modules/helpers/debounce');

// elements
const vimeoElement = '.js-vimeo-player';
const vimeoFullscreen = '.js-vimeo-fullscreen';
const vimeoFullscreenBackdrop = '.js-vimeo-fullscreen-backdrop';
const videoControls = '.js-video-controls';
const videoControlBtn = '.js-video-control-button';
const videoClose = '.js-video-close';

const backgroundVideoElement = '.js-background-video';
const experienceVideoButton = '.js-show-fullscreen';

const ceremonySlider = '.js-product-main-slider';
const ceremonyImageHolder = '.js-product-slider-image-holder';

const contentAssetVideoButton = '.js-content-video-button';
const clickEvent = isMobile() ? 'touchstart.video' : 'click.video';

const players = [];
let onFullscreenHoverTimeout;
let currentFullscreenPlayer;

/**
* @function
* @description Creates video players
*/
function createPlayers() {
    $(vimeoElement).each(function(index, el) {
        const options = $(el).data('vimeo-options');

        // Fix for not looping in background mode
        if (options.background === true && !('loop' in options)) {
            options.loop = 1;
        }

        const shouldInitVideo = (
            (isMobileWidth() && !options.hideOnMobile) ||
            (!isMobileWidth() && !options.hideOnDesktop)
        ) && !options.skipOnLoad;

        if (shouldInitVideo) {
            players[options.id] = createPlayer(el, options);
        }

        if (!isMobileWidth() && options.playWhenInView) {
            playWhenInViewport(options.id);
        }
    });
}

/**
* Private function createPlayer
* Based on: https://github.com/vimeo/player.js
*/
function createPlayer(el, options) {
    const player = new VimeoPlayer($(el), options);

    // specific events for product background video
    if ($(el).closest(backgroundVideoElement).length > 0){
        backgroundVideoEvents(player);
    }

    return player;
}

/**
 * @function
 * @description Centralized private play function
 */
function playVideo(id) {
    const player = players[id];

    if (typeof player !== 'undefined') {
        player.getPaused().then(function(paused) {
            if (paused) {
                player.play().then(function(){
                    // The video was played
                }).catch(function(error) {
                    console.debug('Unable to play video');
                    console.debug(error);
                });
            }
        }).catch(function(error) {
            console.debug('Unable to get the paused state of the player.');
            console.debug(error);
        });
    }
}

/**
 * @function
 * @description Centralized private pause function
 */
function pauseVideo(id) {
    const player = players[id];

    if (typeof player !== 'undefined') {
        player.getPaused().then(function(paused) {
            if (!paused) {
                player.pause().then(function(){
                    // The video was paused
                }).catch(function(error) {
                    console.debug('Unable to pause video');
                    console.debug(error);
                });
            }
        }).catch(function(error) {
            console.debug('Unable to get the paused state of the player.');
            console.debug(error);
        });
    }
}

/**
 * @function
 * @description Initializes Background Video Events
 */
function backgroundVideoEvents(player){
    // on start fade in video
    setTimeout(function () {
        $(backgroundVideoElement).addClass('show');
    }, 700);

    player.ready().then(function() {
        if (typeof player._originalElement !== 'undefined') {
            const bgVideoEl = player._originalElement.offsetParent;
            const controls = $(bgVideoEl).find(videoControls);

            if (controls.length > 0) {
                $(controls).addClass('is-playing');
            }
        }
    }).catch(function(error) {
        console.debug('Unable to get the ready state of the player.');
        console.debug(error);
    });
}

function onClickExperienceButton(){
    const $button = $(this);
    // find nearest fullscreen video
    const videoEl = $button.next(vimeoFullscreen).find(vimeoElement)[0];
    openVideoFullscreen(videoEl);
}

/**
 * @function
 * @description Initializes Background Video Events
 */
function openVideoFullscreen(videoEl, tileId) {
    // Make sure to get the right one to deal with the case where multiple videos are in the same page.

    const $videoElWrapped = $(videoEl);
    const currentVimeoFullscreen = $videoElWrapped.closest(vimeoFullscreen);
    const parentFullwidthBlock = currentVimeoFullscreen.closest('.js-fullwidth-slider-block');
    const parentFullwidthSlider = currentVimeoFullscreen.closest('.js-fullwidth-slider-holder');

    // Check if we actually got one.
    if (currentVimeoFullscreen.length === 0) {
        return;
    }

    // If the popup is in a decorative tile or product slider, append it to body.
    // This is needed in order to show it full screen. Otherwise it would show inside of the tile.
    if (currentVimeoFullscreen.closest('.js-grid-decorative-tile-wrapper').length || currentVimeoFullscreen.closest('.js-product-slider-holder').length || parentFullwidthSlider.length) {
        currentVimeoFullscreen.attr('data-tile-id', tileId);
        currentVimeoFullscreen.appendTo('body');
    }

    if (parentFullwidthBlock.length) {
        parentFullwidthBlock.addClass('video-shown');
    }

    // Parse data object
    const options = $(videoEl).data('vimeo-options');

    // Add video to players array if not exist
    if (typeof players[options.id] === 'undefined') {
        players[options.id] = createPlayer(videoEl, options);
    }

    currentFullscreenPlayer = players[options.id];

    if (isMobile()){
        currentFullscreenPlayer.ready().then(function() {
            currentVimeoFullscreen.addClass('active visible');
        }).catch(function(error) {
            console.debug('Unable to get the ready state of the player.');
            console.debug(error);
        });
    } else {
        currentFullscreenPlayer.ready().then(function() {
            // Find videoElement in vimeoFullscreen element
            const videoEl = currentVimeoFullscreen.find(vimeoElement);

            // Calculate the video width and apply to videoEl
            Promise.all([currentFullscreenPlayer.getVideoWidth(), currentFullscreenPlayer.getVideoHeight()]).then(function(dimensions) {
                const factor = dimensions[0] / dimensions[1];
                videoEl.width(Math.floor(videoEl.height() * factor));
            }).catch(function(error) {
                console.debug('Unable to resize video.');
                console.debug(error);
            });

            currentVimeoFullscreen.data('initial-scroll-position', $(window).scrollTop());
            currentVimeoFullscreen.addClass('active visible');
            $('body').addClass('video-fullscreen');
        }).catch(function(error) {
            console.debug('Unable to get the ready state of the player.');
            console.debug(error);
        });
    }

    // bind scroll event so we can close on scroll
    $(window).on('scroll.video', debounce(closeFullscreenOnWindowScroll, 200));

    // bind accessibility events handler on the active element
    $('body').on('keydown.video', function(e) {
        accessibilityEventsHandler(e, options.id);
    });

    if (!isMobile()) {
        setTimeout(function() {
            playVideo(options.id);
        }, 1000);
    }

    setTimeout(function() {
        $(vimeoFullscreenBackdrop).on('click', closeFullscreenVideo);
    }, 100);
}

/**
 * @function
 * @description enables keyboard functionality for the fullscreen video
 */
function accessibilityEventsHandler(e, id) {
    const player = players[id];

    if (e.keyCode === 13 || e.keyCode === 32){ // enter or space - pause/play
        e.preventDefault();

        // get play/pause state
        player.getPaused().then(function(paused) {
            if (paused){
                playVideo(id);
            } else {
                pauseVideo(id);
            }
        }).catch(function(error) {
            console.debug('Unable to get the paused state of the player.');
            console.debug(error);
        });
    } else if (e.keyCode === 27) { // close video on escape
        e.preventDefault();
        closeFullscreenVideo.call(player._originalElement);
    }
}

/**
 * @function
 * @description Close fullscreen video
 */
function closeFullscreenVideo(evt) {
    if (typeof evt !== 'undefined') {
        evt.preventDefault(); //Prevent the close link from navigating
    }

    // get video inside the same fullscreen video element
    const currentVideoElement = $(this).closest(vimeoFullscreen).find(vimeoElement);
    const videoPlayerOptions = currentVideoElement.data('vimeo-options');
    const id = videoPlayerOptions.id;

    if (typeof players[id] !== 'undefined') {
        pauseVideo(id);

        $(this).closest(videoControls).removeClass('active');
        $(vimeoFullscreen).removeClass('active');
        $('body').removeClass('video-fullscreen');
        setTimeout(function() {
            $(vimeoFullscreen).removeClass('visible');
        }, 400);
    }

    // If the popup is in decorative tile or product slider, append it back to that tile from body.
    const $videoFullScreen = $(this).closest('.js-vimeo-fullscreen[data-tile-id]');

    if ($videoFullScreen.length) {
        const tileId = $videoFullScreen.data('tile-id');

        setTimeout(function() {
            $('button[data-tile-id="' + tileId + '"]').parent().append($videoFullScreen);
        }, 500);
    }

    if ($('.js-fullwidth-slider-block').hasClass('video-shown')) {
        $('.js-fullwidth-slider-block').removeClass('video-shown');
    }

    // unbind onScrollWindow callback from scroll event
    $(window).off('scroll.video');

    // unbind accessibility events handler
    $('body').off('keydown.video');

    $(vimeoFullscreenBackdrop).off('click', closeFullscreenVideo);

    if ($(this).closest('.js-product-experience-block').length) {
        $('.js-drag-list').css('z-index', '0');
    }
}

/**
 * @function
 * @description Handle play button click
 */
function onClickVideoPlayPause() {
    const target = $(this).data('target');
    const controls = $(this).closest(videoControls);
    const id = getVideoId(target);

    if (typeof players[id] !== 'undefined') {
        if (!$(controls).hasClass('is-playing')) {
            // in case the video had been intentionally paused, remove flag
            $(controls).removeClass('was-paused');
            $(controls).addClass('is-playing');
            playVideo(id);
        } else {
            // save that the video was paused - prevents playing on scroll
            $(controls).addClass('was-paused');
            $(controls).removeClass('is-playing');
            pauseVideo(id);
        }
    }
}

/**
 * @function
 * @description Handle fullscreen video mouseover
 */
function onMouseOverFullscreen() {
    const controls = $(this).find(videoControls);
    clearTimeout(onFullscreenHoverTimeout);

    if (!controls.hasClass('active')) {
        controls.addClass('active');
    }
}

/**
 * @function
 * @description Handle fullscreen video mouseout
 */
function onMouseOutFullscreen() {
    const controls = $(this).find(videoControls);
    clearTimeout(onFullscreenHoverTimeout);

    onFullscreenHoverTimeout = setTimeout(handleMouseOutFullscreen, 1000, controls);
}

/**
 * @function
 * @description timed out mouseout handler
 */
function handleMouseOutFullscreen(el) {
    el.removeClass('active');
}

/**
 * @function
 * @description control play/pause on scroll in/out of viewport
 */
function playWhenInViewport(id) {
    const player = players[id];

    $(window).on('load scroll', function() {
        if (typeof player._originalElement !== 'undefined') {
            const bgPlayerEl = player._originalElement.offsetParent;
            const controls = $(bgPlayerEl).find(videoControls);
            const isOpaque = $(bgPlayerEl).parent().css('opacity') == '0';

            // when video is in viewport and is visible
            if (elementInViewport(bgPlayerEl, 0) && !isOpaque) {
                // only play if video wasn't paused by the user before
                if (!$(controls).hasClass('was-paused')) {
                    playVideo(id);
                    controls.addClass('is-playing');
                }
            } else {
                pauseVideo(id);
                controls.removeClass('is-playing');
            }
        }
    });
}

/**
 * @function
 * @description close full screen on scroll
 */
function closeFullscreenOnWindowScroll() {
    const start = $(vimeoFullscreen).data('initial-scroll-position') || 0;
    const offset = 100;

    if ($(window).scrollTop() > (start + offset) || $(window).scrollTop() < (start - offset)) {
        $(videoClose).trigger('click');
    }
}

/**
 * @function
 * @description Finds the player id given a video element
 */
function getVideoId(el) {
    const options = $.parseJSON(JSON.stringify($(el).data('vimeo-options')));
    return options.id;
}

/**
 * @function
 * @description Initializes Experience Video - PDP
 */
function initExperienceVideo(){
    // init events
    $(experienceVideoButton).on('click', onClickExperienceButton);
}

/**
 * @function
 * @description Initializes Steps Block Video - Collection page
 */
function initCeremonyStepsVideo(){
    // if there was a video in this slider, set it to start from the beginning
    if (!isMobileWidth()) {
        $(ceremonySlider).on('beforeChange', function(e, slick, currentSlide, nextSlide) {

            // Get current slide and search for video element
            const currentImageHolderEl = $(ceremonyImageHolder).get(currentSlide);
            const currentVideoEl = $(currentImageHolderEl).find(vimeoElement);
            const currentControlsEl = $(currentImageHolderEl).find(videoControls);

            if (currentVideoEl.length > 0) {
                currentControlsEl.removeClass('is-playing was-paused');

                // get video id and pause it
                const id = getVideoId(currentVideoEl);
                pauseVideo(id);
            }

            // Get next slide and search for video element
            const nextImageHolderEl = $(ceremonyImageHolder).get(nextSlide);
            const nextVideoEl = $(nextImageHolderEl).find(vimeoElement);
            const nextControlsEl = $(nextImageHolderEl).find(videoControls);

            if (nextVideoEl.length > 0) {
                nextControlsEl.addClass('is-playing');

                // get video id and play it from the beginning
                const id = getVideoId(nextVideoEl);

                // set video time to 0
                players[id].setCurrentTime(0);
                playVideo(id);
            }
        });
    }
}

/**
 * @function
 * @description Initializes video in content asset
 */
function initContentAssetVideo() {
    // init events
    $(contentAssetVideoButton).off(clickEvent).on(clickEvent, onClickContentAssetButton);

    // create video's
    $(vimeoFullscreen).find(vimeoElement).each(function(idx, el) {
        const options = $.parseJSON(JSON.stringify($(el).data('vimeo-options')));

        // Add video to players array if not exist
        if (typeof players[options.id] === 'undefined') {
            players[options.id] = createPlayer(el, options);
        }
    });

    // if there is an `openvideo` query param in the url and it's true
    if (getQueryStringParams(window.location.href).openvideo){
        // find first fullscreen video
        const videoEl = $(vimeoFullscreen).first().find(vimeoElement)[0];
        openVideoFullscreen(videoEl);
    }
}

function onClickContentAssetButton(e){
    const $button = $(e.target);
    // find nearest fullscreen video
    const videoEl = $button.next(vimeoFullscreen).find(vimeoElement)[0];
    const buttonDataAttr = $button.data('tile-id');


    openVideoFullscreen(videoEl, buttonDataAttr);

    if ($button.closest('.js-product-experience-block').length) {
        $('.js-drag-list').css('z-index', '100');
    }
}

/**
 * @function
 * @description Initializes the video events Collection pages
 */
function initCollectionPageEvents(){
    // init events
    initCeremonyStepsVideo();
}

/**
 * @function
 * @description Initializes the video events in PDP pages
 */
function initPDPEvents(){
    // init events
    if ($(experienceVideoButton)){
        initExperienceVideo();
    }

}

/**
 * @function
 * @description Initializes the video events for content assets in all pages
 */
function initGenericEvents(){
    // init events
    createPlayers();

    $(vimeoFullscreen).on('mouseover', onMouseOverFullscreen);
    $(vimeoFullscreen).on('mouseout', onMouseOutFullscreen);
    $(videoControlBtn).off('click', onClickVideoPlayPause).on('click', onClickVideoPlayPause);
    $(videoClose).on('click', closeFullscreenVideo);

    closeFullscreenOnWindowScroll();

    // accessibility events
    // pause/play on enter
    $(videoControlBtn).on('keyup', function (e) {
        const target = $(this).data('target');
        const controls = $(this).closest(videoControls);
        const id = getVideoId(target);

        // checking if it actually is KeyboardEvent, because of conflicts with click on the same button
        // without this, it was calling the event twice, then Play/Pause didn't work as it should
        if (typeof players[id] !== 'undefined' && e.originalEvent === KeyboardEvent) {
            if (e.which === 13 || e.which === 32) {
                if (!$(controls).hasClass('is-playing')) {
                    // in case the video had been intentionally paused, remove flag
                    $(controls).removeClass('was-paused');
                    $(controls).addClass('is-playing');
                    playVideo(id);
                } else {
                    // save that the video was paused - prevents playing on scroll
                    $(controls).addClass('was-paused');
                    $(controls).removeClass('is-playing');
                    pauseVideo(id);
                }
            }
        }
    });
}

const video = {
    initGeneric: function(){
        // init video events
        initGenericEvents();
        initContentAssetVideo();
    },
    initPDP: function(){
        initPDPEvents();
    },
    initCollection: function(){
        initCollectionPageEvents();
    }
};


module.exports = video;
